import { APP_VERSION, serverUrl } from "@/config/default";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const TOKEN_KEY = "token";
const ADMIN_ROLE = "admin";

type AdminUser = {
  id: number;
  login: string;
  role: string;
  createdAt: string;
  testsCount: number;
};

// Логин (он же e-mail) и пароль созданной учётной записи.
// Пароль приходит с сервера только в момент создания.
type Credentials = {
  login: string;
  password: string;
};

type Access = "checking" | "login" | "forbidden" | "ready";

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
};

const AdminPanel = () => {
  const [access, setAccess] = useState<Access>("checking");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [credentials, setCredentials] = useState<Credentials | null>(null);

  useEffect(() => {
    document.title = "Администрирование — Скай тест";
    return () => {
      document.title = "Скай тест";
    };
  }, []);

  // Протухший токен — на форму входа, чужая роль — на заглушку без прав
  const deny = useCallback((status: number) => {
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setAccess("login");
    } else {
      setAccess("forbidden");
    }
  }, []);

  const loadUsers = useCallback(
    async (token: string) => {
      setLoadingUsers(true);
      try {
        const res = await fetch(`${serverUrl}admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401 || res.status === 403) {
          deny(res.status);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setUsers(Array.isArray(data.users) ? data.users : []);
        setAccess("ready");
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить список пользователей");
      } finally {
        setLoadingUsers(false);
      }
    },
    [deny]
  );

  // Проверяем сохранённый токен: в панель пускаем только администратора
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAccess("login");
      return;
    }

    fetch(`${serverUrl}isauth`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) {
          localStorage.removeItem(TOKEN_KEY);
          setAccess("login");
          return;
        }
        if (data.role !== ADMIN_ROLE) {
          setAccess("forbidden");
          return;
        }
        return loadUsers(token);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAccess("login");
      });
  }, [loadUsers]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSigningIn(true);

    try {
      const res = await fetch(`${serverUrl}auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Неверный логин или пароль");
        return;
      }

      // Токен не администратора не сохраняем, чтобы не подменять сессию участника теста
      if (data.role !== ADMIN_ROLE) {
        setError("У этой учётной записи нет прав администратора");
        return;
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      setPassword("");
      setAccess("ready");
      await loadUsers(data.token);
    } catch (err) {
      console.error(err);
      setError("Ошибка соединения с сервером");
    } finally {
      setSigningIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUsers([]);
    setCredentials(null);
    setAccess("login");
  };

  // Создание учётной записи: пару логин/пароль генерирует сервер
  const createUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAccess("login");
      return;
    }

    setCreating(true);
    setError("");
    try {
      const res = await fetch(`${serverUrl}admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (res.status === 401 || res.status === 403) {
        deny(res.status);
        return;
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || "Не удалось создать пользователя");
        return;
      }

      setCredentials({ login: data.user.login, password: data.user.password });
      toast("Учётная запись создана");
      await loadUsers(token);
    } catch (err) {
      console.error(err);
      setError("Ошибка соединения с сервером");
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(
        `${credentials.login}\n${credentials.password}`
      );
      toast("Логин и пароль скопированы");
    } catch {
      toast("Скопировать не удалось — сохраните данные вручную");
    }
  };

  if (access === "checking") {
    return (
      <div className="page adminPanel gridCenter w100 h100">
        <div className="adminMuted">Проверка доступа…</div>
      </div>
    );
  }

  if (access === "login" || access === "forbidden") {
    return (
      <div className="page adminPanel gridCenter w100 h100">
        <div className="inner columnCenter gap24">
          <h1>Панель администратора</h1>

          {access === "forbidden" && (
            <div className="adminNotice">
              Текущая учётная запись не является администратором.
            </div>
          )}

          <form className="loginForm columnCenter gap12" onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Логин"
              autoComplete="username"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              disabled={signingIn}
            />
            <input
              type="password"
              placeholder="Пароль"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={signingIn}
            />
            {error && <div className="loginError">{error}</div>}
            <button
              type="submit"
              className="btn mainBtn"
              disabled={signingIn || !login.trim() || !password}
            >
              {signingIn ? "Вход..." : "Войти"}
            </button>
          </form>

          <Link className="linkBtn" to="/">
            Вернуться к тесту
          </Link>
        </div>
        <div className="version">v{APP_VERSION}</div>
      </div>
    );
  }

  return (
    <div className="page adminPanel">
      <div className="adminInner columnCenter gap24">
        <div className="adminHeader between w100">
          <h1>Пользователи</h1>
          <div className="columnCenter gap12">
            <div className="btn mainBtn compact" onClick={createUser}>
              {creating ? "Создание..." : "Создать пользователя"}
            </div>
            <div className="btn linkBtn" onClick={logout}>
              Выйти
            </div>
          </div>
        </div>

        {credentials && (
          <div className="credentialsCard columnCenter gap12">
            <div className="credentialsTitle">Новая учётная запись</div>
            <div className="credentialsRow">
              <span>Логин (e-mail):</span>
              <b>{credentials.login}</b>
            </div>
            <div className="credentialsRow">
              <span>Пароль:</span>
              <b>{credentials.password}</b>
            </div>
            <div className="credentialsHint">
              Пароль показывается один раз — передайте его участнику и закройте
              блок.
            </div>
            <div className="flex gap12">
              <div className="btn mainBtn compact" onClick={copyCredentials}>
                Скопировать пару
              </div>
              <div
                className="btn linkBtn"
                onClick={() => setCredentials(null)}
              >
                Скрыть
              </div>
            </div>
          </div>
        )}

        {error && <div className="loginError">{error}</div>}

        {loadingUsers ? (
          <div className="adminMuted">Загрузка…</div>
        ) : (
          <div className="adminTableWrap w100">
            <table className="adminTable w100">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Логин (e-mail)</th>
                  <th>Роль</th>
                  <th>Тестов</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={user.role === ADMIN_ROLE ? "adminRow" : undefined}
                  >
                    <td>{user.id}</td>
                    <td>{user.login}</td>
                    <td>
                      {user.role === ADMIN_ROLE ? "администратор" : "пользователь"}
                    </td>
                    <td>{user.testsCount}</td>
                    <td>{formatDate(user.createdAt)}</td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="adminMuted">
                      Учётных записей пока нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Link className="linkBtn" to="/">
          К приложению
        </Link>
      </div>
      <div className="version">v{APP_VERSION}</div>
    </div>
  );
};

export default AdminPanel;
